// ============================================================================
// deepLinkReferral.js — PURE helpers for Referral Pings "Open Referral"
// deep-link recovery.
// ----------------------------------------------------------------------------
// A ping's "Open Referral" navigates the user to a role workspace with an
// autoOpenReferralId. Each consumer view historically searched only its current
// tab's loaded list, so the referral often dead-ended in a different tab/bucket.
// These helpers let a view search across ALL of its already-loaded buckets and,
// when the referral is not in any of them, produce a truthful fail-fast message.
//
// PURE: no Firestore, no React, no side effects — node-testable in isolation.
// ============================================================================

export const DEEP_LINK_NOTICE = {
  NOT_FOUND:    'Referral not found.',
  NO_ACCESS:    'You do not have access to this referral.',
  OUT_OF_STAGE: 'This referral exists but is at a stage outside this workspace.',
  NOT_LOADED:   'This referral is not in the current workspace view. It may have moved to another stage or been closed.',
};

// Default id extractor — tolerates the various item shapes used across views
// (work items wrap { referral, workflow }; some lists are flat referral/metric rows).
const defaultGetId = (item) =>
  item?.referral?.id
  ?? item?.referral?.referralId
  ?? item?.referralId
  ?? item?.id
  ?? null;

/**
 * Search an ordered list of buckets for a referral by id.
 * @param {Array<{ tab?: any, items?: any[], getId?: (item:any)=>any, meta?: any }>} buckets
 * @param {string} referralId
 * @returns {{ found: boolean, tab?: any, item?: any, meta?: any }}
 */
export const findReferralInBuckets = (buckets, referralId) => {
  if (!referralId || !Array.isArray(buckets)) return { found: false };
  for (const bucket of buckets) {
    if (!bucket) continue;
    const items = Array.isArray(bucket.items) ? bucket.items : [];
    const getId = typeof bucket.getId === 'function' ? bucket.getId : defaultGetId;
    const item = items.find((it) => getId(it) === referralId);
    if (item) {
      return { found: true, tab: bucket.tab, item, meta: bucket.meta };
    }
  }
  return { found: false };
};

/**
 * Map the outcome of a read-only getReferral() fallback to a specific notice.
 * Pass the cleaned referral doc (null when missing) OR a caught error.
 *   - error with permission/denied → NO_ACCESS
 *   - any other error              → NOT_LOADED
 *   - referral == null             → NOT_FOUND
 *   - referral exists (readable, but not in any loaded bucket of this workspace)
 *                                  → OUT_OF_STAGE
 * @param {{ referral?: any, error?: any }} outcome
 * @returns {string} a DEEP_LINK_NOTICE value
 */
export const classifyDeepLinkFailure = ({ referral, error } = {}) => {
  if (error) {
    const code = String(error?.code || error?.message || '');
    if (/permission|denied|insufficient|unauthor/i.test(code)) return DEEP_LINK_NOTICE.NO_ACCESS;
    return DEEP_LINK_NOTICE.NOT_LOADED;
  }
  if (referral == null) return DEEP_LINK_NOTICE.NOT_FOUND;
  return DEEP_LINK_NOTICE.OUT_OF_STAGE;
};

export default { DEEP_LINK_NOTICE, findReferralInBuckets, classifyDeepLinkFailure };
